<template>
    <Teleport to="body">
        <Transition name="vdp-guide">
            <div v-if="visible" class="vdp-guide" role="dialog" aria-modal="true" @click.self="close">
                <div class="vdp-guide__panel">
                    <header class="vdp-guide__head">
                        <h2 class="vdp-guide__title">{{ title }}</h2>
                        <button
                            type="button"
                            class="vdp-guide__close"
                            :aria-label="$t('Close') || 'Fermer'"
                            @click="close"
                        >
                            <Icon icon="ri:close-line" class="size-5" />
                        </button>
                    </header>
                    <!-- Contenu riche configuré au BO, ASSAINI (DOMPurify) avant rendu. -->
                    <div class="vdp-guide__body" v-html="safeHtml" />
                    <footer class="vdp-guide__foot">
                        <button type="button" class="vdp-guide__ok" @click="close">
                            {{ $t('Validate') || 'OK' }}
                        </button>
                    </footer>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import DOMPurify from 'dompurify';
import { useI18n } from 'vue-i18n';
import { useSiteStore } from '@/stores/site';
import { useStageStore } from '@/stores/stage';

const siteStore = useSiteStore();
const stageStore = useStageStore();
const { locale } = useI18n();

const visible = ref(false);

const guide = computed(() => siteStore.site?.vdpGuide ?? null);

/** Contenu du guide pour la langue de navigation (repli : 1re langue dispo). */
const localized = computed(() => {
    const g = guide.value;
    if (!g?.locales) {
        return null;
    }
    const code = String(locale.value || 'en')
        .slice(0, 2)
        .toLowerCase();
    return g.locales[code] ?? g.locales.en ?? Object.values(g.locales)[0] ?? null;
});

const title = computed(() => localized.value?.title || '');
const safeHtml = computed(() => DOMPurify.sanitize(localized.value?.html || ''));

/** Le guide s'applique-t-il au parcours courant (expert vs simplifié) ? */
function modeMatches(): boolean {
    const mode = guide.value?.mode || 'both';
    if (mode === 'both') {
        return true;
    }
    const simplified = !!stageStore.simplifiedMode;
    return mode === 'simplified' ? simplified : !simplified;
}

/** Clé de session (par site) : le guide ne s'affiche qu'une fois par session. */
function sessionKey(): string {
    return `pxl-vdp-guide-seen:${siteStore.site?.id || 'default'}`;
}

function close() {
    visible.value = false;
}

// Déclenché par l'hôte via PostMessage vdp:true (stageStore.vdpGuideTrigger).
watch(
    () => stageStore.vdpGuideTrigger,
    (v) => {
        if (!v) {
            return;
        }
        const g = guide.value;
        if (!g?.enabled || !modeMatches()) {
            return;
        }
        if (!localized.value || (!localized.value.title && !localized.value.html)) {
            return;
        }
        try {
            if (sessionStorage.getItem(sessionKey()) === '1') {
                return; // déjà vu cette session
            }
            sessionStorage.setItem(sessionKey(), '1');
        } catch {
            /* sessionStorage indisponible : on affiche quand même */
        }
        visible.value = true;
    }
);
</script>

<style lang="scss" scoped>
.vdp-guide {
    position: fixed;
    inset: 0;
    z-index: 10001;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(23, 26, 31, 0.6);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}
.vdp-guide__panel {
    width: min(92vw, 560px);
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    background: var(--nu-surface, #fff);
    border-radius: var(--nu-radius, 16px);
    box-shadow: var(--nu-shadow-lg, 0 24px 56px rgba(23, 26, 31, 0.16));
    overflow: hidden;
}
.vdp-guide__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid var(--nu-line, #e4e7ec);
}
.vdp-guide__title {
    font-size: 17px;
    font-weight: 800;
    color: var(--nu-ink, #171a1f);
}
.vdp-guide__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: var(--nu-ink-soft, #4a4f58);
    cursor: pointer;
    &:hover {
        background: var(--nu-magenta-wash, rgba(236, 30, 120, 0.08));
        color: var(--nu-magenta, #ec1e78);
    }
}
.vdp-guide__body {
    padding: 18px;
    overflow-y: auto;
    font-size: 14px;
    line-height: 1.55;
    color: var(--nu-ink-soft, #4a4f58);

    :deep(h1),
    :deep(h2),
    :deep(h3) {
        color: var(--nu-ink, #171a1f);
        font-weight: 700;
        margin: 0.6em 0 0.3em;
    }
    :deep(ul),
    :deep(ol) {
        padding-left: 1.4em;
        margin: 0.4em 0;
    }
    :deep(a) {
        color: var(--nu-magenta, #ec1e78);
        text-decoration: underline;
    }
    :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
    }
    :deep(p) {
        margin: 0.4em 0;
    }
}
.vdp-guide__foot {
    padding: 12px 18px 16px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid var(--nu-line, #e4e7ec);
}
.vdp-guide__ok {
    min-width: 120px;
    height: 44px;
    padding: 0 22px;
    border-radius: 999px;
    border: none;
    font-weight: 800;
    font-size: 15px;
    color: #fff;
    background: var(--nu-magenta, #ec1e78);
    cursor: pointer;
    &:hover {
        filter: brightness(1.05);
    }
}

.vdp-guide-enter-active,
.vdp-guide-leave-active {
    transition: opacity 200ms ease;
}
.vdp-guide-enter-from,
.vdp-guide-leave-to {
    opacity: 0;
}
</style>
